import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "../../models/db";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "../utils";
import { Task, TaskInstance, User } from "./index";

// Interfaces
export interface DueDateOffset {
  days?: number;
  hours?: number;
  minutes?: number;
}

export interface TemplateTask extends BaseDbObject {
  template_id: string;
  title: string;
  description?: string;
  auto_assigned_user_ids?: string[];
  auto_assigned_users?: User[];
  due_date_offset: DueDateOffset;
  order_index?: number;
}

export interface TaskTemplate extends BaseDbObject {
  name: string;
  description?: string;
  active: boolean;
  created_by_id: string;
  created_by?: User;
  tasks?: TemplateTask[];
}

export interface TaskTemplateInstance extends BaseDbObject {
  template_id: string;
  template?: TaskTemplate;
  triggered_by_id: string;
  triggered_by?: User;
  trigger_source: string;
  trigger_meta_data?: any;
  triggered_by_user?: { id: string; name: string; };
  task_instances?: TaskInstance[];
}

export interface TemplateHistory {
  id: string;
  template_id: string;
  changed_by_id: string;
  changed_by?: User;
  change_type: string;
  old_value?: any;
  new_value?: any;
  created_at: string;
}

export interface TemplateInstanceHistory {
  id: string;
  template_instance_id: string;
  changed_by_id: string;
  changed_by?: User;
  change_type: string;
  old_value?: any;
  new_value?: any;
  created_at: string;
}

// Request/Response Types
export const CreateTemplateTaskSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  auto_assigned_user_ids: z.array(z.string().uuid()).optional(),
  due_date_offset: z.object({
    days: z.number().optional(),
    hours: z.number().optional(),
    minutes: z.number().optional(),
  }),
});

export const CreateTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});

export type CreateTemplateRequest = z.infer<typeof CreateTemplateSchema>;

export const UpdateTemplateSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  active: z.boolean().optional(),
});

export type UpdateTemplateRequest = z.infer<typeof UpdateTemplateSchema>;

export const UpdateTemplateTaskSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  auto_assigned_user_ids: z.array(z.string().uuid()).optional(),
  due_date_offset: z.object({
    days: z.number().optional(),
    hours: z.number().optional(),
    minutes: z.number().optional(),
  }).optional(),
  order_index: z.number().optional(),
});

export type UpdateTemplateTaskRequest = z.infer<typeof UpdateTemplateTaskSchema>;

export const ReorderTemplateTasksSchema = z.object({
  task_ids: z.array(z.string().uuid()).min(1),
});

export type ReorderTemplateTasksRequest = z.infer<typeof ReorderTemplateTasksSchema>;

export const TriggerTemplateSchema = z.object({
  trigger_source: z.string().min(1),
  trigger_meta_data: z.any().optional(),
});

export type TriggerTemplateRequest = z.infer<typeof TriggerTemplateSchema>;

export interface ListTemplatesParams {
  page?: number;
  limit?: number;
  active?: boolean;
  search?: string;
}

export type ListTemplatesResponse = TaskTemplate[];

export interface ListTemplateInstancesParams {
  page?: number;
  limit?: number;
  template_id?: string;
  triggered_by_id?: string;
  trigger_source?: string;
  created_at_from?: string;
  created_at_to?: string;
  search?: string;
}

export interface ListTemplateInstancesResponse {
  instances: TaskTemplateInstance[];
  total: number;
  page: number;
  limit: number;
}

export interface TemplateDetailResponse extends TaskTemplate {
  tasks: TemplateTask[];
  histories: TemplateHistory[];
}

// API Hooks - Template Management
export function useListTemplates(params?: ListTemplatesParams) {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.limit) queryParams.set("limit", params.limit.toString());
    if (params.active !== undefined) queryParams.set("active", params.active.toString());
    if (params.search) queryParams.set("search", params.search);
  }

  return useQuery({
    queryKey: ["templates", "list", queryParams.toString()],
    queryFn: () => fetchApiGet<ListTemplatesResponse>("templates", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetTemplate(templateId: string) {
  return useQuery({
    queryKey: ["templates", "detail", templateId],
    queryFn: () => fetchApiGet<TemplateDetailResponse>(`templates/${templateId}`),
    retry: 2,
    enabled: !!templateId,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      fetchApiPost<TaskTemplate>("templates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useUpdateTemplate(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) =>
      fetchApiPut<TaskTemplate>(`templates/${templateId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) =>
      fetchApiDelete(`templates/${templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// API Hooks - Template Task Management
export function useGetTemplateTask(templateId: string, taskId: string) {
  return useQuery({
    queryKey: ["templates", templateId, "tasks", taskId],
    queryFn: () => fetchApiGet<TemplateTask>(`templates/${templateId}/tasks/${taskId}`),
    retry: 2,
    enabled: !!templateId && !!taskId,
  });
}

export function useCreateTemplateTask(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: z.infer<typeof CreateTemplateTaskSchema>) =>
      fetchApiPost<TemplateTask>(`templates/${templateId}/tasks`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useUpdateTemplateTask(templateId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTemplateTaskRequest) =>
      fetchApiPut<TemplateTask>(`templates/${templateId}/tasks/${taskId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useDeleteTemplateTask(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) =>
      fetchApiDelete(`templates/${templateId}/tasks/${taskId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useReorderTemplateTasks(templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderTemplateTasksRequest) =>
      fetchApiPut(`templates/${templateId}/tasks/reorder`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// API Hooks - Template Instance Management
export function useListTemplateInstances(params?: ListTemplateInstancesParams) {
  const queryParams = new URLSearchParams();

  if (params) {
    if (params.page) queryParams.set("page", params.page.toString());
    if (params.limit) queryParams.set("limit", params.limit.toString());
    if (params.template_id) queryParams.set("template_id", params.template_id);
    if (params.triggered_by_id) queryParams.set("triggered_by_id", params.triggered_by_id);
    if (params.trigger_source) queryParams.set("trigger_source", params.trigger_source);
    if (params.created_at_from) queryParams.set("created_at_from", params.created_at_from);
    if (params.created_at_to) queryParams.set("created_at_to", params.created_at_to);
    if (params.search) queryParams.set("search", params.search);
  }

  return useQuery({
    queryKey: ["templates", "instances", "list", queryParams.toString()],
    queryFn: () => fetchApiGet<ListTemplateInstancesResponse>("templates/template-instances", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetTemplateInstance(instanceId: string) {
  return useQuery({
    queryKey: ["templates", "instances", "detail", instanceId],
    queryFn: () => fetchApiGet<TaskTemplateInstance>(`templates/template-instances/${instanceId}`),
    retry: 2,
    enabled: !!instanceId,
  });
}

export function useGetTemplateInstanceTasks(instanceId: string) {
  return useQuery({
    queryKey: ["templates", "instances", instanceId, "tasks"],
    queryFn: () => fetchApiGet<TaskInstance[]>(`templates/template-instances/${instanceId}/tasks`),
    retry: 2,
    enabled: !!instanceId,
  });
}

export function useTriggerTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: TriggerTemplateRequest }) =>
      fetchApiPost<TaskTemplateInstance>(`templates/${templateId}/trigger`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
